import { Alert, AlertDescription, AlertTitle } from "@bednbody/ui/components/alert";
import { cn } from "@bednbody/ui/lib/utils";
import { InfoIcon, type Icon } from "@phosphor-icons/react";
import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps, ReactNode } from "react";

const calloutVariants = cva("min-w-0", {
  variants: {
    tone: {
      neutral: "border-border bg-muted/40",
      info: "border-info/30 bg-info/5 [&>svg]:text-info",
      warning: "border-warning/30 bg-warning/5 [&>svg]:text-warning",
      success: "border-success/30 bg-success/5 [&>svg]:text-success",
      danger: "border-destructive/30 bg-destructive/5 [&>svg]:text-destructive",
    },
  },
  defaultVariants: { tone: "neutral" },
});

export type CalloutProps = Omit<ComponentProps<typeof Alert>, "title" | "children"> &
  VariantProps<typeof calloutVariants> & {
    title?: ReactNode;
    children: ReactNode;
    icon?: Icon;
  };

/** Static guidance is a note. Callers opt into role=alert for an urgent dynamic error. */
export function Callout({ title, children, icon: CalloutIcon = InfoIcon, tone, className, role = "note", ...props }: CalloutProps) {
  return (
    <Alert {...props} role={role} className={cn(calloutVariants({ tone }), className)}>
      <CalloutIcon aria-hidden weight="duotone" />
      {title != null ? <AlertTitle>{title}</AlertTitle> : null}
      <AlertDescription className="min-w-0 wrap-anywhere">{children}</AlertDescription>
    </Alert>
  );
}
